import type { ExerciseTemplate } from '@yuan-math/exercises';
import { add, fraction, gcd, lcm, mul, toLatex, toText } from '@yuan-math/math';
import {
  choiceExercise,
  expr,
  fractionExercise,
  fractionTrap,
  leadingTerm,
  numericExercise,
  options,
  pickDistinct,
  signedConstant,
  signedTerm,
  template,
  trap,
} from './authoring.ts';

/**
 * Complex numbers, polynomial division and polynomial functions, then rational
 * functions and radical equations.
 *
 * Two of these templates are built backwards from the wrong answer rather than
 * from the right one, because that is the only way to make the diagnosis exact.
 * `polynomial-quotient-coefficient` chooses a cubic with no x² term so that
 * dropping the placeholder produces a specific predictable number, and
 * `radical-extraneous` chooses the quadratic so that exactly one of its two
 * roots survives substitution. In both cases a vaguely-wrong distractor would
 * have taught the student nothing.
 */

/** An imaginary term for the front of an expression: `3i`, `-i`, `0`. */
const leadingImaginary = (b: number): string => (b === 0 ? '0' : leadingTerm(b, 'i'));

/**
 * A number for prose rather than for LaTeX.
 *
 * `-4` inside a formula is right, and `-4` in a sentence is a hyphen standing
 * where a minus sign belongs. Prompts and explanations render as text, so they
 * need the real character.
 */
const minus = (value: number | string): string => String(value).replace(/-/g, '\u2212');

/**
 * A linear factor: `(x - 3)`, `(x + 2)` — or a bare `x` when the root is zero.
 *
 * `(x - 0)` and, worse, `(x )` are not factors anyone writes, and a generated
 * root of zero is not rare enough to leave to luck.
 */
const rootFactor = (root: number): string => (root === 0 ? 'x' : `(x ${signedConstant(-root)})`);

export const ALGEBRA_TWO_TEMPLATES: ExerciseTemplate[] = [
  /* ------------------------------------------------------ complex numbers */
  template({
    id: 'complex-root-of-negative',
    conceptId: 'complex-numbers',
    description: 'Write the square root of a negative number using i.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const b = rng.int(2, 15);
      const radicand = b * b;

      return numericExercise({
        prompt: `Write √(−${radicand}) in the form bi. What is b?`,
        latex: `\\sqrt{-${radicand}}`,
        value: b,
        summary: `√(−${radicand}) = √${radicand} · √(−1) = ${b}i, so b is ${b}.`,
        hint: 'Split off √(−1), which is i. What is left is an ordinary square root.',
        steps: [
          { label: 'Separate the −1.', latex: `\\sqrt{${radicand}} \\cdot \\sqrt{-1}` },
          { label: 'Take the root of the positive part.', latex: `${b} \\cdot i` },
        ],
        // The student who ignores the minus sign and reports a real root.
        traps: [trap(-b, 'negative-radicand-taken-real')],
      });
    },
  }),

  template({
    id: 'complex-add',
    conceptId: 'complex-numbers',
    description: 'Add or subtract complex numbers, keeping the parts separate.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const limit = difficulty === 3 ? 14 : 9;
      const a = rng.int(1, limit);
      const b = rng.int(1, limit);
      const c = rng.int(1, limit);
      const d = rng.int(1, limit);
      const subtract = rng.bool();
      const real = subtract ? a - c : a + c;
      const imaginary = subtract ? b - d : b + d;

      return numericExercise({
        prompt: 'Simplify. What is the real part of the answer?',
        latex: `(${a} ${signedTerm(b, 'i')}) ${subtract ? '-' : '+'} (${c} ${signedTerm(d, 'i')})`,
        value: real,
        summary: `Real with real and imaginary with imaginary: ${a} ${subtract ? '−' : '+'} ${c} = ${real}, and the answer is ${expr(String(real), signedTerm(imaginary, 'i'))}.`,
        hint: 'The two parts never mix. Deal with the plain numbers on their own.',
        steps: [
          { label: 'Collect the real parts.', latex: `${a} ${subtract ? '-' : '+'} ${c} = ${real}` },
          {
            label: 'Collect the imaginary parts.',
            latex: `${leadingTerm(b, 'i')} ${subtract ? '-' : '+'} ${leadingTerm(d, 'i')} = ${leadingImaginary(imaginary)}`,
          },
        ],
        // Adding the two parts together, as though they were the same kind of thing.
        traps: [...(imaginary === 0 ? [] : [trap(real + imaginary, 'complex-parts-combined')])],
      });
    },
  }),

  template({
    id: 'complex-multiply',
    conceptId: 'complex-numbers',
    description: 'Multiply complex numbers, replacing i² with −1.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const limit = difficulty === 4 ? 9 : 6;
      const a = rng.int(1, limit);
      const b = rng.int(1, limit);
      const c = rng.int(1, limit);
      const d = rng.int(1, limit);
      const real = a * c - b * d;
      const imaginary = a * d + b * c;

      return numericExercise({
        prompt: 'Multiply out and simplify. What is the real part of the answer?',
        latex: `(${a} ${signedTerm(b, 'i')})(${c} ${signedTerm(d, 'i')})`,
        value: real,
        summary: `Expanding gives ${a * c} + ${imaginary}i + ${b * d}i², and i² = −1 turns that last term into −${b * d}. The real part is ${a * c} − ${b * d} = ${real}.`,
        hint: 'Expand as you would with brackets in x, then replace i² with −1.',
        steps: [
          {
            label: 'Expand every pair.',
            latex: `${a * c} + ${leadingTerm(a * d, 'i')} + ${leadingTerm(b * c, 'i')} + ${leadingTerm(b * d, 'i^{2}')}`,
          },
          { label: 'Replace i² with −1.', latex: `${a * c} - ${b * d} + ${leadingTerm(imaginary, 'i')}` },
          { label: 'Collect the real part.', latex: `${real}` },
        ],
        // Leaving i² as +1, which flips the sign of the last term.
        traps: [trap(a * c + b * d, 'i-squared-taken-positive')],
      });
    },
  }),

  template({
    id: 'complex-power-of-i',
    conceptId: 'complex-numbers',
    description: 'Simplify a power of i using i² = −1.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      /*
       * Only exponents where i² = −1 actually matters. At n ≡ 0 or 1 (mod 4) a
       * student who believes i² = +1 reaches the right answer by the wrong
       * route, and the item would diagnose nothing.
       */
      const n = rng.pick([2, 3, 6, 7, 10, 11, 14, 15, 18, 19, 22, 23, 26, 27, 30, 31, 34, 35, 38, 39, 42, 43, 46, 47]);
      const remainder = n % 4;
      const correct = remainder === 2 ? '-1' : '-i';
      const believedPositive = remainder === 2 ? '1' : 'i';

      const { choices, correctId } = options(
        [
          { latex: correct, correct: true },
          { latex: believedPositive, misconceptionId: 'i-squared-taken-positive' },
          { latex: remainder === 2 ? 'i' : '-1' },
          { latex: remainder === 2 ? '-i' : '1' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: `Simplify i^${n}.`,
        latex: `i^{${n}}`,
        choices,
        correctId,
        summary: `The powers of i repeat every four: i, −1, −i, 1. ${n} leaves a remainder of ${remainder} on division by 4, so i^${n} = ${correct}.`,
        hint: 'i⁴ = 1, so throw away whole groups of four and see what is left.',
      });
    },
  }),

  template({
    id: 'complex-discriminant',
    conceptId: 'complex-numbers',
    description: 'Read a negative discriminant as two complex roots.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const p = rng.int(-9, 9) || 2;
      const complexRoots = rng.bool(0.6);
      // q is chosen to put the discriminant on the wanted side of zero.
      const floor = Math.ceil((p * p) / 4);
      const q = complexRoots ? floor + rng.int(1, 9) : Math.min(floor - 1, 0) - rng.int(0, 8);
      // A negative coefficient has to be bracketed before it is squared:
      // "-8²" reads as −64, which is not the number being described.
      const squared = p < 0 ? `(${p})²` : `${p}²`;

      return numericExercise({
        prompt: 'How many real solutions does this equation have?',
        latex: expr('x^2', signedTerm(p), signedConstant(q), '= 0'),
        value: complexRoots ? 0 : 2,
        summary: complexRoots
          ? `The discriminant is ${squared} − 4(${q}) = ${p * p - 4 * q}, which is negative. There are no real solutions — the two roots are complex.`
          : `The discriminant is ${squared} − 4(${q}) = ${p * p - 4 * q}, which is positive, so there are two real solutions.`,
        hint: 'Work out b² − 4ac. Its sign is the whole answer.',
        steps: [
          { label: 'Find the discriminant.', latex: `${p < 0 ? `(${p})` : p}^{2} - 4(${q}) = ${p * p - 4 * q}` },
          {
            label: complexRoots ? 'It is negative, so the roots are complex.' : 'It is positive, so both roots are real.',
            latex: complexRoots ? '0 \\text{ real}' : '2 \\text{ real}',
          },
        ],
        // Taking the root of a negative as though it were a real number.
        traps: [...(complexRoots ? [trap(2, 'negative-radicand-taken-real')] : [])],
      });
    },
  }),

  /* --------------------------------------------------- polynomial division */
  template({
    id: 'remainder-theorem',
    conceptId: 'polynomial-division',
    description: 'Find a remainder by substitution rather than by dividing.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const a = rng.int(2, difficulty === 4 ? 6 : 4) * (rng.bool() ? 1 : -1);
      const b = rng.int(-6, 6) || 3;
      const c = rng.int(-9, 9) || 4;
      const d = rng.int(-9, 9) || 5;
      const at = (x: number): number => x ** 3 + b * x * x + c * x + d;
      const remainder = at(a);
      const flipped = at(-a);

      return numericExercise({
        prompt: `What is the remainder when this is divided by (x ${a < 0 ? '+' : '-'} ${Math.abs(a)})?`,
        latex: expr('x^3', signedTerm(b, 'x^2'), signedTerm(c), signedConstant(d)),
        value: remainder,
        summary: `The divisor is zero at x = ${a}, so the remainder is P(${a}) = ${remainder}.`,
        hint: 'Set the bracket equal to zero to find which value to substitute.',
        steps: [
          { label: 'Find the value that makes the divisor zero.', latex: `x = ${a}` },
          { label: 'Substitute it into the polynomial.', latex: `P(${a}) = ${remainder}` },
        ],
        // Substituting the number as it appears, rather than the root of the divisor.
        traps: [...(flipped === remainder ? [] : [trap(flipped, 'remainder-theorem-sign-flipped')])],
      });
    },
  }),

  template({
    id: 'factor-theorem-identify',
    conceptId: 'polynomial-division',
    description: 'Pick the linear factor of a cubic from its roots.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const root = rng.int(1, 7) * (rng.bool() ? 1 : -1);
      const other = rng.int(1, 6);
      const third = rng.int(1, 6) + other;
      /*
       * The two decoy brackets are drawn from what is left after the root and
       * its mirror image are removed, and zero is never in the pool: "(x − 0)"
       * both reads as nonsense and trips the degenerate-term test.
       */
      const decoys = rng.shuffle(
        [-9, -8, -7, -6, -5, -4, -3, -2, -1, 1, 2, 3, 4, 5, 6, 7, 8, 9].filter(
          (value) => value !== root && value !== -root,
        ),
      );
      // (x − root)(x − other)(x + third), expanded.
      const roots = [root, other, -third];
      const sum = roots[0]! + roots[1]! + roots[2]!;
      const pairs = roots[0]! * roots[1]! + roots[0]! * roots[2]! + roots[1]! * roots[2]!;
      const product = roots[0]! * roots[1]! * roots[2]!;

      const bracket = (value: number): string => `(x ${value < 0 ? '+' : '-'} ${Math.abs(value)})`;

      const { choices, correctId } = options(
        [
          { latex: bracket(root), correct: true },
          { latex: bracket(-root), misconceptionId: 'remainder-theorem-sign-flipped' },
          { latex: bracket(decoys[0] as number) },
          { latex: bracket(decoys[1] as number) },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: `P(${root}) = 0. Which of these must be a factor of P(x)?`,
        latex: expr('P(x) = x^3', signedTerm(-sum, 'x^2'), signedTerm(pairs), signedConstant(-product)),
        choices,
        correctId,
        summary: `The factor theorem says P(a) = 0 exactly when (x − a) is a factor. Here a = ${root}, so the factor is ${bracket(root)}.`,
        hint: 'Which bracket is zero when x takes the value that makes P zero?',
      });
    },
  }),

  template({
    id: 'polynomial-quotient-coefficient',
    conceptId: 'polynomial-division',
    description: 'Divide a cubic with a missing degree, where the placeholder matters.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      /*
       * Built with no x² term on purpose. Dividing x³ + cx + d by (x − a) gives
       * the quotient x² + ax + (c + a²), so the coefficient asked for is a —
       * while a student who omits the empty x² column divides the three
       * remaining coefficients and lands on exactly 1, every time. That makes
       * the diagnosis certain rather than plausible.
       */
      const a = rng.pick([2, 3, 4, 5, 6, -2, -3, -4, -5, -6]);
      const chosen = rng.int(-9, 9) || 3;
      // c = −a² would empty both the quotient's constant term and the dividend's.
      const c = chosen === -(a * a) ? chosen + 1 : chosen;
      const d = -(a ** 3) - a * c;

      return numericExercise({
        prompt: `Divide this by (x ${a < 0 ? '+' : '-'} ${Math.abs(a)}). What is the coefficient of x in the quotient?`,
        latex: expr('x^3', signedTerm(c), signedConstant(d)),
        value: a,
        summary: `Writing the missing x² term as 0 and dividing gives x² ${signedTerm(a)} ${signedConstant(c + a * a)}, so the coefficient of x is ${a}.`,
        hint: 'There is no x² term written down. Give it a place anyway, with a coefficient of 0.',
        steps: [
          // The `0x^2` is the lesson, not a stray zero term: the step exists to
          // show the empty column being written down. Do not tidy it away.
          { label: 'Hold the empty place.', latex: expr('x^3 + 0x^2', signedTerm(c), signedConstant(d)) },
          { label: 'Divide.', latex: expr('x^2', signedTerm(a), signedConstant(c + a * a)) },
          { label: 'Read off the coefficient of x.', latex: `${a}` },
        ],
        // Dropping the empty column shifts everything along, and always lands on 1.
        traps: [trap(1, 'missing-degree-not-held')],
      });
    },
  }),

  /* -------------------------------------------------- polynomial functions */
  template({
    id: 'end-behaviour',
    conceptId: 'polynomial-functions',
    description: 'Predict end behaviour from the leading term alone.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const degree = rng.pick([3, 4, 5, 6]);
      const lead = rng.int(1, 6) * (rng.bool() ? 1 : -1);
      const middle = rng.int(-9, 9) || 4;
      const even = degree % 2 === 0;
      const up = lead > 0;
      /*
       * For an even degree the constant is given the opposite sign to the
       * leading coefficient, so that the distractor built from it is always a
       * different answer. A distractor that happens to be correct diagnoses
       * nothing and confuses everything.
       */
      const constant = rng.int(1, 9) * (even ? (up ? -1 : 1) : rng.bool() ? 1 : -1);

      const correct = even
        ? up
          ? 'Both ends go up'
          : 'Both ends go down'
        : up
          ? 'Down on the left, up on the right'
          : 'Up on the left, down on the right';

      const { choices, correctId } = options(
        [
          { label: correct, correct: true },
          {
            // What the constant term suggests, rather than the leading term.
            label: constant > 0 ? 'Both ends go up' : 'Both ends go down',
            misconceptionId: 'end-behaviour-from-wrong-term',
          },
          { label: up ? 'Up on the left, down on the right' : 'Down on the left, up on the right' },
          { label: 'The ends level off and stay flat' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: 'What happens to this curve far to the left and far to the right?',
        latex: expr(leadingTerm(lead, `x^${degree}`), signedTerm(middle, 'x^2'), signedConstant(constant)),
        choices,
        correctId,
        summary: `Only the leading term ${leadingTerm(lead, `x^${degree}`)} matters far from the origin. Degree ${degree} is ${even ? 'even, so both ends go the same way' : 'odd, so the ends go opposite ways'}, and the leading coefficient is ${lead > 0 ? 'positive' : 'negative'}.`,
        hint: 'At x = 100 the highest power dwarfs every other term. Look only at that one.',
      });
    },
  }),

  template({
    id: 'root-count-with-multiplicity',
    conceptId: 'polynomial-functions',
    description: 'Count roots with multiplicity rather than counting distinct ones.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const p = rng.int(1, 9);
      const q = rng.int(1, 9) + p;
      const r = rng.int(1, 9);
      const power = rng.int(2, difficulty === 4 ? 4 : 3);
      // Three distinct roots however large the multiplicity gets, so the wrong
      // answer a student who ignores multiplicity gives is always 3.
      const distinct = 3;
      const total = 1 + power + 1;

      return numericExercise({
        prompt: 'How many roots does this polynomial have, counted with multiplicity?',
        latex: `y = (x - ${p})(x - ${q})^{${power}}(x + ${r})`,
        value: total,
        summary: `The middle factor is repeated ${power} times, so ${q} is a root ${power} times over. Altogether that is 1 + ${power} + 1 = ${total}.`,
        hint: 'A repeated factor is a repeated root. Add the powers.',
        steps: [
          { label: 'Read the multiplicity of each factor.', latex: `1, \\; ${power}, \\; 1` },
          { label: 'Add them — this is also the degree.', latex: `${total}` },
        ],
        // Counting the three distinct roots and stopping there.
        traps: [trap(distinct, 'multiplicity-ignored')],
      });
    },
  }),

  template({
    id: 'touch-or-cross',
    conceptId: 'polynomial-functions',
    description: 'Decide whether a curve crosses or touches at a repeated root.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const p = rng.int(1, 9);
      const q = rng.int(1, 9) + p;
      const power = rng.pick([2, 3, 4]);
      const even = power % 2 === 0;

      const { choices, correctId } = options(
        [
          {
            label: even ? `It touches the axis at x = ${q} and turns back` : `It crosses the axis at x = ${q}`,
            correct: true,
          },
          {
            label: even ? `It crosses the axis at x = ${q}` : `It touches the axis at x = ${q} and turns back`,
            misconceptionId: 'multiplicity-ignored',
          },
          { label: `It never reaches the axis at x = ${q}` },
          { label: `It has an asymptote at x = ${q}` },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: `What does this curve do at x = ${q}?`,
        latex: `y = (x - ${p})(x - ${q})^{${power}}`,
        choices,
        correctId,
        summary: even
          ? `The factor is repeated an even number of times (${power}), so the curve meets the axis and turns back without crossing.`
          : `The factor is repeated an odd number of times (${power}), so the curve passes through the axis.`,
        hint: 'Count how many times the factor appears. Even and odd behave differently.',
      });
    },
  }),

  /* --------------------------------------------------- rational functions */
  template({
    id: 'vertical-asymptote',
    conceptId: 'rational-functions',
    description: 'Find a vertical asymptote from the denominator, not the numerator.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const numeratorRoot = rng.int(1, 9) * (rng.bool() ? 1 : -1);
      // Drawn from a pool rather than nudged on collision: nudging −1 upwards
      // lands on zero, and "(x − 0)" is not something anyone writes.
      const denominatorRoot = rng.pick(
        [-9, -8, -7, -6, -5, -4, -3, -2, -1, 1, 2, 3, 4, 5, 6, 7, 8, 9].filter((value) => value !== numeratorRoot),
      );

      const bracket = (root: number): string => `x ${root < 0 ? '+' : '-'} ${Math.abs(root)}`;

      return numericExercise({
        prompt: 'At what value of x does this function have a vertical asymptote?',
        latex: `f(x) = \\frac{${bracket(numeratorRoot)}}{${bracket(denominatorRoot)}}`,
        value: denominatorRoot,
        summary: `The function is undefined where the denominator is zero, which is x = ${denominatorRoot}.`,
        hint: 'An asymptote is where the function breaks down. Which part of a fraction cannot be zero?',
        steps: [
          { label: 'Set the denominator to zero.', latex: `${bracket(denominatorRoot)} = 0` },
          { label: 'Solve.', latex: `x = ${denominatorRoot}` },
        ],
        // Solving the top instead — that finds the root, not the break.
        traps: [trap(numeratorRoot, 'asymptote-from-numerator')],
      });
    },
  }),

  template({
    id: 'hole-or-asymptote',
    conceptId: 'rational-functions',
    description: 'Tell a cancelled factor from a surviving one.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const shared = rng.int(1, 8);
      const numeratorRoot = rng.int(1, 8) + shared;
      let denominatorRoot = rng.int(-8, -1);
      if (denominatorRoot === -shared) denominatorRoot -= 1;

      const bracket = (root: number): string => `(x ${root < 0 ? '+' : '-'} ${Math.abs(root)})`;

      const { choices, correctId } = options(
        [
          { label: `A hole — the factor cancels`, correct: true },
          { label: `A vertical asymptote`, misconceptionId: 'hole-called-asymptote' },
          { label: `A root of the function`, misconceptionId: 'asymptote-from-numerator' },
          { label: `Nothing at all — the function is defined there` },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: `What does this function have at x = ${shared}?`,
        latex: `f(x) = \\frac{${bracket(shared)}${bracket(numeratorRoot)}}{${bracket(shared)}${bracket(denominatorRoot)}}`,
        choices,
        correctId,
        summary: `${bracket(shared)} appears on the top and the bottom, so it cancels. The input x = ${shared} is still forbidden, but the graph has only one point missing there rather than an asymptote.`,
        hint: 'Cancel first. A factor that survives on the bottom behaves differently from one that does not.',
      });
    },
  }),

  template({
    id: 'horizontal-asymptote-zero',
    conceptId: 'rational-functions',
    description: 'Compare degrees to find a horizontal asymptote of y = 0.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const bottom = rng.int(2, 9);
      // A whole multiple, so the distractor is a tidy number rather than a
      // decimal nobody would type.
      const top = bottom * rng.int(1, 4);
      const constant = rng.int(1, 9);

      return numericExercise({
        prompt: 'What is the horizontal asymptote of this function? Give the value of y.',
        latex: `f(x) = \\frac{${top}x + ${constant}}{${bottom}x^{2} + ${constant}}`,
        value: 0,
        summary: `The bottom has the higher degree, so it grows faster and the fraction is squeezed towards 0. The asymptote is y = 0.`,
        hint: 'Which grows faster as x gets large — the top or the bottom?',
        steps: [
          { label: 'Compare the degrees.', latex: '1 < 2' },
          { label: 'The bottom wins, so the fraction shrinks.', latex: 'y = 0' },
        ],
        // Comparing the leading coefficients, which is only right at equal degrees.
        traps: [trap(top / bottom, 'end-behaviour-from-wrong-term')],
      });
    },
  }),

  template({
    id: 'horizontal-asymptote-ratio',
    conceptId: 'rational-functions',
    description: 'Find a horizontal asymptote when the degrees match.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      // All the x-coefficients start at 2, because "1x" is not how anyone
      // writes it and the content tests say so.
      const top = rng.int(2, 9);
      const bottom = rng.int(2, 9);
      const b = rng.int(2, 9);
      const c = rng.int(1, 9);

      return fractionExercise({
        prompt: 'What is the horizontal asymptote of this function? Give the value of y.',
        latex: `f(x) = \\frac{${top}x^{2} + ${b}x}{${bottom}x^{2} + ${c}}`,
        value: fraction(top, bottom),
        requireSimplified: true,
        // `toText`, not `toLatex` — a summary is rendered as plain prose, so a
        // \frac{}{} would reach the student as literal backslashes.
        summary: `The degrees match, so far out the fraction settles at the ratio of the leading coefficients: ${toText(fraction(top, bottom))}.`,
        hint: 'When top and bottom grow at the same rate, the lower-order terms stop mattering.',
        steps: [
          { label: 'Compare the degrees.', latex: '2 = 2' },
          { label: 'Take the ratio of the leading coefficients.', latex: `y = ${toLatex(fraction(top, bottom))}` },
        ],
        // Assuming every rational function flattens towards zero.
        traps: [fractionTrap(fraction(0), 'end-behaviour-from-wrong-term')],
      });
    },
  }),

  /* ---------------------------------------------------- radical equations */
  template({
    id: 'radical-solve-basic',
    conceptId: 'radical-equations',
    description: 'Solve a square-root equation by isolating and squaring.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const result = rng.int(2, difficulty === 4 ? 11 : 7);
      const outside = rng.int(1, 9);
      const shift = rng.int(-9, 9) || 4;
      // √(x + shift) + outside = result + outside, so x = result² − shift.
      const answer = result * result - shift;

      return numericExercise({
        prompt: 'Solve for x.',
        latex: `\\sqrt{x ${shift < 0 ? '-' : '+'} ${Math.abs(shift)}} + ${outside} = ${result + outside}`,
        value: answer,
        summary: `Subtracting ${outside} leaves √(x ${shift < 0 ? '−' : '+'} ${Math.abs(shift)}) = ${result}. Squaring gives x ${shift < 0 ? '−' : '+'} ${Math.abs(shift)} = ${result * result}, so x = ${answer}.`,
        hint: 'The root is not alone yet. Deal with everything outside it first.',
        steps: [
          { label: 'Isolate the radical.', latex: `\\sqrt{x ${shift < 0 ? '-' : '+'} ${Math.abs(shift)}} = ${result}` },
          { label: 'Square both sides.', latex: `x ${shift < 0 ? '-' : '+'} ${Math.abs(shift)} = ${result * result}` },
          { label: 'Solve.', latex: `x = ${answer}` },
        ],
        /*
         * Squaring while the root still has something added to it: the student
         * writes x + shift + outside² = (result + outside)², which rearranges
         * to this value.
         */
        traps: [trap((result + outside) ** 2 - outside * outside - shift, 'root-isolated-too-late')],
      });
    },
  }),

  template({
    id: 'radical-extraneous',
    conceptId: 'radical-equations',
    description: 'Solve a radical equation where one candidate has to be discarded.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      /*
       * Chosen so the squared equation has two integer roots and exactly one of
       * them survives. With √(x + c) = x − t, squaring gives
       * x² − (2t+1)x + (t² − c) = 0; picking the larger root first fixes c.
       */
      const t = rng.int(0, 4);
      const first = t + rng.int(2, 8);
      // c = 0 would print "√(x + 0)", so the larger root is nudged past it.
      const valid = t * t - first * (2 * t + 1 - first) === 0 ? first + 1 : first;
      const extraneous = 2 * t + 1 - valid;
      const c = t * t - valid * extraneous;

      return numericExercise({
        prompt: 'Solve for x. Only one of the candidates survives.',
        latex: `\\sqrt{x ${c < 0 ? '-' : '+'} ${Math.abs(c)}} = ${t === 0 ? 'x' : `x - ${t}`}`,
        value: valid,
        summary: `Squaring gives ${expr('x²', signedTerm(-(2 * t + 1)), signedConstant(t * t - c), '= 0')}, whose roots are ${valid} and ${extraneous}. Substituting ${extraneous} makes the right-hand side negative, and a square root never is — so it is discarded, leaving x = ${valid}.`,
        hint: 'Square, solve the quadratic, then put both answers back into the original equation.',
        steps: [
          { label: 'Square both sides.', latex: `x ${c < 0 ? '-' : '+'} ${Math.abs(c)} = (${t === 0 ? 'x' : `x - ${t}`})^{2}` },
          { label: 'Rearrange and solve.', latex: `x = ${valid} \\text{ or } x = ${extraneous}` },
          { label: 'Check each in the original.', latex: `x = ${valid} \\;\\checkmark, \\quad x = ${extraneous} \\;\\times` },
        ],
        // The candidate the squaring invented, kept because it was never checked.
        traps: [trap(extraneous, 'extraneous-root-kept')],
      });
    },
  }),

  template({
    id: 'radical-no-solution',
    conceptId: 'radical-equations',
    description: 'Recognise a radical equation with no solution at all.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const impossible = rng.bool();
      const k = rng.int(2, 15);
      const shift = rng.int(1, 9);

      return numericExercise({
        prompt: 'How many solutions does this equation have?',
        latex: `\\sqrt{x + ${shift}} = ${impossible ? '-' : ''}${k}`,
        value: impossible ? 0 : 1,
        summary: impossible
          ? `A square root is never negative, so nothing can make this true. There are no solutions.`
          : `Squaring gives x + ${shift} = ${k * k}, so x = ${k * k - shift}. That checks out, so there is exactly one solution.`,
        hint: 'Before doing any algebra, ask whether the right-hand side is a value a square root can take.',
        steps: [
          {
            label: impossible ? 'The right-hand side is negative.' : 'The right-hand side is positive.',
            latex: impossible ? '\\text{no solution}' : `x = ${k * k - shift}`,
          },
        ],
        // Squaring anyway, and keeping the value that comes out.
        traps: [...(impossible ? [trap(1, 'extraneous-root-kept')] : [])],
      });
    },
  }),

  template({
    id: 'radical-first-step',
    conceptId: 'radical-equations',
    description: 'Choose the correct first step: isolate before squaring.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const outside = rng.int(2, 12);
      const total = outside + rng.int(2, 12);

      const { choices, correctId } = options(
        [
          { label: `Subtract ${outside} from both sides`, correct: true },
          { label: 'Square both sides straight away', misconceptionId: 'root-isolated-too-late' },
          { label: `Divide both sides by ${outside}`, misconceptionId: 'root-isolated-too-late' },
          { label: `Subtract ${outside} from the right-hand side only` },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: 'What is the correct first step in solving this equation?',
        latex: `\\sqrt{x} + ${outside} = ${total}`,
        choices,
        correctId,
        summary: `Squaring now would give x + ${2 * outside}√x + ${outside * outside}, which still contains a root. Get the radical alone first: √x = ${total - outside}.`,
        hint: 'Squaring only removes a root that is standing by itself.',
      });
    },
  }),

  /* ---------------------------------------------------- rational exponents */
  template({
    id: 'rational-exponent-evaluate',
    conceptId: 'rational-exponents',
    description: 'Evaluate a number raised to a rational power.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const index = rng.pick([2, 3]);
      const root = rng.int(2, index === 2 ? (difficulty === 3 ? 9 : 5) : 4);
      // Coprime, or the exponent prints as 4/2 and answers its own question.
      const power = rng.pick([2, 3, 4, 5].filter((m) => gcd(m, index) === 1 && root ** m <= 4000));
      const base = root ** index;
      const answer = root ** power;
      const multiplied = (base * power) / index;

      return numericExercise({
        prompt: 'Evaluate.',
        latex: `${base}^{\\frac{${power}}{${index}}}`,
        value: answer,
        summary: `The denominator is the root and the numerator is the power. The ${index === 2 ? 'square' : 'cube'} root of ${base} is ${root}, and ${root} to the power ${power} is ${answer}.`,
        hint: 'Take the root first — the numbers stay small that way — and only then apply the power.',
        steps: [
          { label: 'Read the denominator as a root.', latex: `\\sqrt[${index}]{${base}} = ${root}` },
          { label: 'Read the numerator as a power.', latex: `${root}^{${power}} = ${answer}` },
        ],
        traps: [
          // The root taken, the numerator never used.
          trap(root, 'rational-exponent-power-dropped'),
          ...(Number.isInteger(multiplied) ? [trap(multiplied, 'rational-exponent-multiplied')] : []),
        ],
      });
    },
  }),

  template({
    id: 'rational-exponent-from-radical',
    conceptId: 'rational-exponents',
    description: 'Rewrite a radical as a single rational exponent.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const index = rng.int(2, 5);
      const power = rng.pick([2, 3, 4, 5, 6, 7].filter((m) => m !== index && gcd(m, index) === 1));

      return fractionExercise({
        prompt: 'Write this as a single power of x. What is the exponent?',
        latex: `\\sqrt[${index}]{x^{${power}}}`,
        value: fraction(power, index),
        requireSimplified: true,
        summary: `The index goes underneath. The ${index}th root of x to the power ${power} is x to the power ${power}/${index}.`,
        hint: 'One of the two numbers tells you the root and the other the power. Which is which?',
        steps: [
          {
            label: 'The index becomes the denominator.',
            latex: `\\sqrt[${index}]{x^{${power}}} = x^{\\frac{${power}}{${index}}}`,
          },
        ],
        // Index and power exchanged — the one slip this topic is made of.
        traps: [fractionTrap(fraction(index, power), 'rational-exponent-parts-swapped')],
      });
    },
  }),

  template({
    id: 'rational-exponent-negative',
    conceptId: 'rational-exponents',
    description: 'Evaluate a negative rational exponent as a reciprocal.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const index = rng.pick([2, 3]);
      const root = rng.int(2, index === 2 ? 6 : 4);
      const power = rng.pick([1, 2, 3].filter((m) => gcd(m, index) === 1 && root ** m <= 200));
      const base = root ** index;

      return fractionExercise({
        prompt: 'Evaluate, and give your answer as a fraction.',
        latex: `${base}^{-\\frac{${power}}{${index}}}`,
        value: fraction(1, root ** power),
        requireSimplified: true,
        summary: `The minus sign means "one over". Without it the answer would be ${root ** power}, so with it the answer is 1/${root ** power}.`,
        hint: 'A negative exponent never makes an answer negative. It turns it upside down.',
        steps: [
          {
            label: 'Flip it, because the exponent is negative.',
            latex: `\\frac{1}{${base}^{\\frac{${power}}{${index}}}}`,
          },
          { label: 'Evaluate the bottom.', latex: `\\frac{1}{${root ** power}}` },
        ],
        traps: [
          // A negative exponent read as a negative answer.
          fractionTrap(fraction(-(root ** power)), 'negative-exponent-negative-value'),
          ...(power > 1 ? [fractionTrap(fraction(1, root), 'rational-exponent-power-dropped')] : []),
        ],
      });
    },
  }),

  template({
    id: 'rational-exponent-product',
    conceptId: 'rational-exponents',
    description: 'Multiply two powers of x with fractional exponents.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const firstDenominator = rng.int(2, 5);
      const secondDenominator = rng.int(2, 5);
      const firstNumerator = rng.pick([1, 2, 3, 4].filter((n) => n < firstDenominator && gcd(n, firstDenominator) === 1));
      const secondNumerator = rng.pick(
        [1, 2, 3, 4].filter((n) => n < secondDenominator && gcd(n, secondDenominator) === 1),
      );
      const left = fraction(firstNumerator, firstDenominator);
      const right = fraction(secondNumerator, secondDenominator);

      return fractionExercise({
        prompt: 'Simplify to a single power of x. What is the exponent?',
        latex: `x^{${toLatex(left)}} \\cdot x^{${toLatex(right)}}`,
        value: add(left, right),
        requireSimplified: true,
        summary: `Multiplying powers of the same base adds the exponents, whatever kind of number they are: ${toText(left)} + ${toText(right)} = ${toText(add(left, right))}.`,
        hint: 'The rule did not change because the exponents became fractions.',
        steps: [{ label: 'Add the exponents.', latex: `${toLatex(left)} + ${toLatex(right)} = ${toLatex(add(left, right))}` }],
        // The rule for a power of a power, applied to a product of two powers.
        traps: [fractionTrap(mul(left, right), 'exponents-multiplied-not-added')],
      });
    },
  }),

  template({
    id: 'rational-exponent-radical-form',
    conceptId: 'rational-exponents',
    description: 'Match a rational exponent to the radical that means the same thing.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const index = rng.int(2, 4);
      const power = rng.pick([2, 3, 4, 5].filter((m) => m !== index && gcd(m, index) === 1));

      const { choices, correctId } = options(
        [
          { latex: `\\sqrt[${index}]{x^{${power}}}`, correct: true },
          { latex: `\\sqrt[${power}]{x^{${index}}}`, misconceptionId: 'rational-exponent-parts-swapped' },
          { latex: `\\frac{${power}}{${index}} \\cdot x`, misconceptionId: 'rational-exponent-multiplied' },
          { latex: `\\sqrt[${index * power}]{x}` },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: 'Which of these means the same thing?',
        latex: `x^{\\frac{${power}}{${index}}}`,
        choices,
        correctId,
        summary: `The denominator ${index} is the index of the root and the numerator ${power} is the power, so this is the ${index}th root of x to the power ${power}.`,
        hint: 'Bottom of the fraction, bottom of the root sign.',
      });
    },
  }),

  /* ----------------------------------------------- polynomial inequalities */
  template({
    id: 'quadratic-inequality-solution',
    conceptId: 'polynomial-inequalities',
    description: 'Solve a factored quadratic inequality and describe the solution set.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const one = pickDistinct(rng, 6, []);
      const other = pickDistinct(rng, 6, [one]);
      const low = Math.min(one, other);
      const high = Math.max(one, other);
      const below = rng.bool();

      const between = `${low} < x < ${high}`;
      const outside = `x < ${low} \\text{ or } x > ${high}`;

      const { choices, correctId } = options(
        [
          { latex: below ? between : outside, correct: true },
          { latex: below ? outside : between, misconceptionId: 'interval-sign-assumed' },
          { latex: `x = ${low} \\text{ or } x = ${high}`, misconceptionId: 'roots-given-for-solution-set' },
          { latex: `x > ${low}` },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: 'Which values of x satisfy this inequality?',
        latex: `${rootFactor(low)}${rootFactor(high)} ${below ? '<' : '>'} 0`,
        choices,
        correctId,
        summary: below
          ? `A product is negative only when its two factors have opposite signs, and that happens only between the roots: ${low} < x < ${high}.`
          : `A product is positive when the factors share a sign — both negative to the left of ${low}, both positive to the right of ${high}.`,
        hint: 'The roots cut the line into three stretches. Test one number from each and see what sign comes out.',
      });
    },
  }),

  template({
    id: 'cubic-sign-interval',
    conceptId: 'polynomial-inequalities',
    description: 'Read the sign of a cubic between its roots, from the graph.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      // Kept an integer apart so the drawn curve turns visibly between roots.
      const first = rng.int(-3, -1);
      const second = rng.int(first + 1, 1);
      const third = rng.int(second + 1, 3);

      const { choices, correctId } = options(
        [
          { latex: `${second} < x < ${third}`, correct: true },
          { latex: `${first} < x < ${second}`, misconceptionId: 'interval-sign-assumed' },
          { latex: `x > ${third}` },
          { latex: `x = ${first} \\text{ or } x = ${third}`, misconceptionId: 'roots-given-for-solution-set' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: 'On which of these intervals is p(x) negative?',
        latex: `p(x) = ${rootFactor(first)}${rootFactor(second)}${rootFactor(third)}`,
        figure: { kind: 'function-graph', roots: [first, second, third], min: -4, max: 4 },
        choices,
        correctId,
        summary: `Between ${second} and ${third} exactly one of the three factors is negative, so the product is negative — which is the graph dipping below the axis there.`,
        hint: 'Count how many factors are negative on each stretch. An odd number of them makes the product negative.',
      });
    },
  }),

  template({
    id: 'quadratic-inequality-integer-count',
    conceptId: 'polynomial-inequalities',
    description: 'Count the integers satisfying an inequality that includes its endpoints.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const low = rng.int(-7, 2);
      const high = low + rng.int(3, 8);
      const answer = high - low + 1;

      return numericExercise({
        prompt: 'How many integers satisfy this inequality?',
        latex: `${rootFactor(low)}${rootFactor(high)} \\le 0`,
        value: answer,
        summary: `The solution is ${minus(low)} ≤ x ≤ ${minus(high)}. Counting the whole numbers from ${minus(low)} to ${minus(high)}, both ends included, gives ${answer}.`,
        hint: 'Solve it first, then count — and check whether the two ends belong to the answer.',
        steps: [
          { label: 'Solve the inequality.', latex: `${low} \\le x \\le ${high}` },
          { label: 'Count inclusively.', latex: `${high} - (${low}) + 1 = ${answer}` },
        ],
        traps: [
          // Counting the gap between the ends rather than the values in it.
          trap(high - low, 'inequality-endpoint-included'),
          // The two roots offered as though they were the solution.
          trap(2, 'roots-given-for-solution-set'),
        ],
      });
    },
  }),

  template({
    id: 'rational-inequality-first-step',
    conceptId: 'polynomial-inequalities',
    description: 'Choose a first step that does not depend on the sign of the denominator.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const shift = rng.int(2, 9);
      const offset = rng.int(1, 8);
      const bound = rng.int(2, 5);

      const { choices, correctId } = options(
        [
          { label: `Subtract ${bound}, then write the left-hand side as one fraction`, correct: true },
          { label: `Multiply both sides by x − ${shift}`, misconceptionId: 'inequality-multiplied-by-unknown-sign' },
          {
            label: `Cross-multiply to get x + ${offset} < ${bound}(x − ${shift})`,
            misconceptionId: 'inequality-multiplied-by-unknown-sign',
          },
          { label: 'Solve the matching equation and give those values of x', misconceptionId: 'roots-given-for-solution-set' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: 'Which first step is safe here?',
        latex: `\\frac{x + ${offset}}{x - ${shift}} < ${bound}`,
        choices,
        correctId,
        summary: `x − ${shift} is negative below ${shift} and positive above it, so multiplying by it would reverse the inequality some of the time and not the rest. Move everything to one side instead, and read the sign of the single fraction that results.`,
        hint: 'Multiplying an inequality by a negative number flips it. Do you know the sign of that denominator?',
      });
    },
  }),

  /* --------------------------------------------------- rational equations */
  template({
    id: 'rational-equation-clear-denominators',
    conceptId: 'rational-equations',
    description: 'Clear two unlike denominators by multiplying every term.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const [smaller, larger] = rng.pick([
        [2, 3],
        [2, 5],
        [3, 4],
        [3, 5],
        [4, 6],
        [4, 5],
        [2, 7],
        [3, 6],
        [5, 6],
        [6, 8],
      ]) as [number, number];
      const common = lcm(smaller, larger);
      const parts = common / smaller + common / larger;
      const multiple = rng.int(1, 6);
      const total = parts * multiple;
      const answer = common * multiple;

      return numericExercise({
        prompt: 'Solve for x.',
        latex: `\\frac{x}{${smaller}} + \\frac{x}{${larger}} = ${total}`,
        value: answer,
        summary: `Multiplying every term by ${common} gives ${leadingTerm(common / smaller)} + ${leadingTerm(common / larger)} = ${total * common}, so ${parts}x = ${total * common} and x = ${answer}.`,
        hint: `Multiply the whole equation by ${common} — including the ${total} on the right, which is the term people leave behind.`,
        steps: [
          {
            label: `Multiply every term by ${common}.`,
            latex: `${leadingTerm(common / smaller)} + ${leadingTerm(common / larger)} = ${total * common}`,
          },
          { label: 'Collect and divide.', latex: `${parts}x = ${total * common}, \\quad x = ${answer}` },
        ],
        // The right-hand side left untouched while the denominators were cleared.
        traps: [trap(multiple, 'denominator-dropped-not-multiplied')],
      });
    },
  }),

  template({
    id: 'rational-equation-like-denominators',
    conceptId: 'rational-equations',
    description: 'Solve an equation whose two fractions already share a denominator.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const target = rng.int(2, 6);
      const half = rng.int(1, 5);
      const total = 2 * target * half;
      const first = rng.int(1, total - 1);
      const second = total - first;

      return numericExercise({
        prompt: 'Solve for x.',
        latex: `\\frac{${first}}{x} + \\frac{${second}}{x} = ${target}`,
        value: 2 * half,
        summary: `The denominators already match, so the numerators add: ${first} + ${second} = ${total}. Then ${total}/x = ${target}, and x = ${2 * half}.`,
        hint: 'Same denominator means the tops add directly. The bottom does not change.',
        steps: [
          { label: 'Add the numerators.', latex: `\\frac{${total}}{x} = ${target}` },
          { label: 'Multiply by x, then divide.', latex: `x = \\frac{${total}}{${target}} = ${2 * half}` },
        ],
        // Denominators added along with the numerators, leaving 2x underneath.
        traps: [trap(half, 'add-denominators')],
      });
    },
  }),

  template({
    id: 'rational-equation-excluded-root',
    conceptId: 'rational-equations',
    description: 'Solve a rational equation where one candidate is outside the domain.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const shift = rng.int(2, 12);
      const subtracted = rng.bool();
      // x ∓ shift underneath, so x = ±shift is the value never allowed.
      const excluded = subtracted ? shift : -shift;
      const answer = -excluded;
      const denominator = `x ${subtracted ? '-' : '+'} ${shift}`;

      return numericExercise({
        prompt: 'Solve for x. One of the two candidates has to be thrown away.',
        latex: `\\frac{x^{2}}{${denominator}} = \\frac{${shift * shift}}{${denominator}}`,
        value: answer,
        summary: `The denominators are identical, so x² = ${shift * shift} and x is ${shift} or −${shift}. But x = ${minus(excluded)} makes that denominator zero, and the original equation has no value at all there — leaving x = ${minus(answer)}.`,
        hint: 'Before solving anything, ask which value of x this equation was never allowed to have.',
        steps: [
          { label: 'Equal denominators, so equal numerators.', latex: `x^{2} = ${shift * shift}` },
          { label: 'Two candidates.', latex: `x = ${shift} \\text{ or } x = -${shift}` },
          {
            label: 'Check both against the denominator.',
            latex: `x = ${answer} \\;\\checkmark, \\quad x = ${excluded} \\;\\times`,
          },
        ],
        // The candidate that empties the denominator, kept because it solved the
        // cleared equation.
        traps: [trap(excluded, 'excluded-value-accepted')],
      });
    },
  }),

  template({
    id: 'rational-equation-count',
    conceptId: 'rational-equations',
    description: 'Decide whether a rational equation has a solution at all.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const forbidden = pickDistinct(rng, 8, []);
      const multiplier = rng.int(2, 5);
      const none = rng.bool();
      // The root that would make the numerator's constant vanish is excluded
      // too, because "x + 0" on the page is a term that is not there.
      const root = pickDistinct(rng, 9, [forbidden, (forbidden * multiplier) / (multiplier - 1)]);
      // A numerator identical to the denominator makes the fraction 1 everywhere.
      const constant = none ? forbidden : root * (1 - multiplier) + forbidden * multiplier;

      return numericExercise({
        prompt: 'How many solutions does this equation have?',
        latex: `\\frac{x ${signedConstant(-constant)}}{x ${signedConstant(-forbidden)}} = ${multiplier}`,
        value: none ? 0 : 1,
        summary: none
          ? `Top and bottom are the same expression, so the left-hand side is 1 everywhere it is defined — and 1 is not ${multiplier}. There are no solutions.`
          : `Multiplying up gives x ${signedConstant(-constant)} = ${multiplier}(x ${signedConstant(-forbidden)}), which solves to x = ${root}. That is not ${forbidden}, so it survives the domain check: one solution.`,
        hint: `Whatever else happens, x = ${minus(forbidden)} is not allowed. Solve, then check your answer against that.`,
        steps: [
          {
            label: 'Multiply both sides by the denominator.',
            latex: `x ${signedConstant(-constant)} = ${multiplier}(x ${signedConstant(-forbidden)})`,
          },
          {
            label: none ? 'Every x cancels, and what is left is false.' : 'Solve, then check the domain.',
            latex: none ? '\\text{no solution}' : `x = ${root}`,
          },
        ],
        // Solving to x = the excluded value and counting it anyway.
        traps: [...(none ? [trap(1, 'excluded-value-accepted')] : [])],
      });
    },
  }),

  template({
    id: 'rational-equation-method',
    conceptId: 'rational-equations',
    description: 'Choose a first step that clears denominators without inventing a rule.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const numerator = rng.int(2, 9);
      const denominator = rng.pick([3, 4, 5, 6]);
      const right = rng.int(2, 9);

      const { choices, correctId } = options(
        [
          { label: `Multiply every term by ${denominator}x`, correct: true },
          {
            label: `Cross-multiply: ${numerator} × ${denominator} = ${right} × x`,
            misconceptionId: 'cross-multiplied-across-a-sum',
          },
          {
            label: `Cancel the denominators to get ${numerator} + 1 = ${right}`,
            misconceptionId: 'denominator-dropped-not-multiplied',
          },
          { label: 'Add the numerators, and add the denominators', misconceptionId: 'add-denominators' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: 'What is the right way to start?',
        latex: `\\frac{${numerator}}{x} + \\frac{1}{${denominator}} = ${right}`,
        choices,
        correctId,
        summary: `${denominator}x is the common denominator, and multiplying *every* term by it — the ${right} included — clears both fractions in one move, leaving an equation with no fractions in it at all.`,
        hint: 'Cross-multiplication is for one fraction equal to one fraction. This left-hand side is a sum of two.',
      });
    },
  }),

  /* -------------------------------------------------- systems with a curve */
  template({
    id: 'line-parabola-sum',
    conceptId: 'nonlinear-systems',
    description: 'Find where a line cuts a parabola, and use both intersections.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const one = pickDistinct(rng, 5, []);
      const other = pickDistinct(rng, 5, [one, -one]);
      const slope = one + other;
      const intercept = -one * other;

      return numericExercise({
        prompt: 'The line cuts the parabola twice. What is the sum of the x-coordinates of those two points?',
        latex: expr('y = x^{2} \\quad y =', leadingTerm(slope), signedConstant(intercept)),
        value: slope,
        summary: `Two expressions for y means they are equal to each other, and the quadratic that gives factorises as ${rootFactor(one)}${rootFactor(other)} = 0. The intersections are at x = ${one} and x = ${other}, which add to ${slope}.`,
        hint: 'Set the two right-hand sides equal. Both intersections are roots of the quadratic that appears.',
        steps: [
          { label: 'Set the right-hand sides equal.', latex: expr('x^{2} =', leadingTerm(slope), signedConstant(intercept)) },
          { label: 'Move everything across and factorise.', latex: `${rootFactor(one)}${rootFactor(other)} = 0` },
          { label: 'Add the two roots.', latex: `(${one}) + (${other}) = ${slope}` },
        ],
        traps: [
          // One root found, and the search stopped there.
          trap(one, 'second-intersection-dropped'),
          trap(other, 'second-intersection-dropped'),
        ],
      });
    },
  }),

  template({
    id: 'line-parabola-count',
    conceptId: 'nonlinear-systems',
    description: 'Count the intersections of a line and a parabola from the discriminant.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const wanted = rng.pick([0, 1, 2]);
      // A tangent needs m²/4 to be a whole number, or the curve prints a decimal.
      const slope = wanted === 1 ? rng.pick([-6, -4, -2, 2, 4, 6]) : pickDistinct(rng, 6, []);
      const square = slope * slope;
      const lift = wanted === 1 ? square / 4 : wanted === 2 ? -rng.int(1, 6) : Math.floor(square / 4) + rng.int(1, 5);
      const discriminant = square - 4 * lift;
      const count = discriminant > 0 ? 2 : discriminant === 0 ? 1 : 0;

      return numericExercise({
        prompt: 'How many points do these two graphs have in common?',
        latex: expr('y = x^{2}', signedConstant(lift), '\\quad y =', leadingTerm(slope)),
        value: count,
        summary: `Setting them equal gives ${expr('x^{2}', signedTerm(-slope), signedConstant(lift), '= 0')}, whose discriminant is ${square} − 4(${lift}) = ${discriminant} — ${count === 2 ? 'positive, so the line cuts the curve twice' : count === 1 ? 'zero, so the line is a tangent and touches once' : 'negative, so the line misses the curve entirely'}.`,
        hint: 'You do not have to find the points. The discriminant of the quadratic counts them for you.',
        steps: [
          { label: 'Set them equal.', latex: expr('x^{2}', signedTerm(-slope), signedConstant(lift), '= 0') },
          { label: 'Take the discriminant.', latex: `(${slope})^{2} - 4(${lift}) = ${discriminant}` },
        ],
        // A negative or zero discriminant read as though it were positive.
        traps: [...(count === 2 ? [] : [trap(2, 'discriminant-sign-misread')])],
      });
    },
  }),

  template({
    id: 'line-parabola-point',
    conceptId: 'nonlinear-systems',
    description: 'Find the y-coordinate at one of two intersections.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const anchor = rng.int(2, 6);
      const partner = pickDistinct(rng, 5, [anchor, -anchor]);
      const low = Math.min(anchor, partner);
      const high = Math.max(anchor, partner);
      const slope = low + high;
      const intercept = -low * high;

      return numericExercise({
        prompt: 'These graphs meet twice. What is the y-coordinate of the point further to the right?',
        latex: expr('y = x^{2} \\quad y =', leadingTerm(slope), signedConstant(intercept)),
        value: high * high,
        summary: `The intersections are at x = ${minus(low)} and x = ${minus(high)}. The one further right is x = ${minus(high)}, and the parabola puts that at y = ${high * high}.`,
        hint: 'Find both x values first, take the larger, and only then work out y.',
        steps: [
          { label: 'Solve for the two x values.', latex: `x = ${low} \\text{ or } x = ${high}` },
          { label: 'Substitute the larger one.', latex: `y = (${high})^{2} = ${high * high}` },
        ],
        traps: [
          // The other intersection used instead.
          trap(low * low, 'second-intersection-dropped'),
          // The x-coordinate handed in before it was finished with.
          ...(low * low === high ? [] : [trap(high, 'intermediate-answer-given')]),
        ],
      });
    },
  }),

  template({
    id: 'line-circle-meet',
    conceptId: 'nonlinear-systems',
    description: 'Intersect a horizontal line with a circle.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const [height, across, radius] = rng.pick([
        [3, 4, 5],
        [4, 3, 5],
        [6, 8, 10],
        [8, 6, 10],
        [5, 12, 13],
        [12, 5, 13],
        [9, 12, 15],
        [12, 9, 15],
        [8, 15, 17],
        [15, 8, 17],
        [7, 24, 25],
        [20, 21, 29],
      ]) as [number, number, number];

      return numericExercise({
        prompt: 'The line meets the circle at two points. What is the positive x-coordinate?',
        latex: `x^{2} + y^{2} = ${radius * radius} \\quad y = ${height}`,
        value: across,
        summary: `Substituting y = ${height} gives x² = ${radius * radius} − ${height * height} = ${across * across}, so x is ${across} or −${across}.`,
        hint: 'The line already tells you y. Put that straight into the circle.',
        steps: [
          { label: 'Substitute y.', latex: `x^{2} + ${height * height} = ${radius * radius}` },
          { label: 'Solve for x.', latex: `x^{2} = ${across * across}, \\quad x = \\pm ${across}` },
        ],
        // Subtracting before rooting, as though the root came apart termwise.
        traps: [trap(radius - height, 'root-distributed-over-sum')],
      });
    },
  }),

  template({
    id: 'nonlinear-system-method',
    conceptId: 'nonlinear-systems',
    description: 'Choose a method that survives one of the two equations being curved.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const bend = pickDistinct(rng, 5, []);
      const lift = pickDistinct(rng, 8, []);
      const slope = pickDistinct(rng, 4, []);
      const offset = pickDistinct(rng, 8, []);

      const { choices, correctId } = options(
        [
          { label: 'Set the two right-hand sides equal and solve the quadratic', correct: true },
          {
            label: 'Subtract one equation from the other to eliminate the x² term',
            misconceptionId: 'curve-eliminated-as-if-linear',
          },
          {
            label: 'Pick a point on the line and check that it satisfies the line',
            misconceptionId: 'system-satisfies-one-equation',
          },
          { label: 'Add the two equations together' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: 'Which method finds every point these two graphs share?',
        latex: expr(
          'y = x^{2}',
          signedTerm(bend),
          signedConstant(lift),
          '\\quad y =',
          leadingTerm(slope),
          signedConstant(offset),
        ),
        choices,
        correctId,
        summary:
          'Both equations already give y on its own, so the x values where the graphs agree are exactly the roots of the quadratic you get by equating the right-hand sides. Elimination has nothing to work with: the line carries no x² term to cancel against.',
        hint: 'Elimination cancels terms that match. Does the line have anything to cancel that x² with?',
      });
    },
  }),
];
